import { Reveal } from "@/components/Reveal";
import { PageHero } from "@/components/PageHero";
import { Eyebrow } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Sprout, Handshake, BookOpen, Gift, Store } from "lucide-react";

const VALUES = [
  { icon: Sprout, title: "Responsible stewardship", text: "We seek to steward resources, relationships, and opportunities carefully — honoring the trust placed in this work." },
  { icon: Handshake, title: "Service", text: "We come alongside churches, ministries, artists, and business owners as servants, not spectators." },
  { icon: BookOpen, title: "Discipleship", text: "We value growth in Christ — helping believers mature in faith and live it out in every sphere." },
  { icon: Gift, title: "Generosity", text: "We believe generosity multiplies. What is given in faith can strengthen many others." },
];

export default function Mission() {
  usePageMeta("Our Mission",
    "We believe churches, ministries, artists, and businesses can work together to strengthen the body of Christ.");
  return (
    <div>
      <PageHero
        eyebrow="Our Mission"
        title="Working together to strengthen the body of Christ"
        subtitle="We believe churches, ministries, artists, and Christ-centered businesses are not meant to stand alone. When they support one another, faith flourishes and communities are strengthened."
        image="/ju-jerusalem.webp"
      />

      <section className="mx-auto max-w-4xl px-5 lg:px-8 py-20 lg:py-24">
        <Reveal>
          <Eyebrow>What we believe</Eyebrow>
          <h2 className="mt-3 font-display text-4xl font-semibold text-navy tracking-tight">A connected body, a shared calling</h2>
          <div className="mt-6 space-y-5 text-muted-foreground leading-relaxed">
            <p>
              The Church is a body of many parts. A closing congregation's building can still be a
              lighthouse. A growing ministry can reach further with encouragement. An artist can bless
              the Church with their gifts. And a business owned by a believer can sustain families,
              create opportunity, and fuel Kingdom generosity.
            </p>
            <p>
              Supporting Christian businesses is not about commerce for its own sake. When owners are
              equipped and connected, they are better able to serve their communities, give generously,
              and contribute to the advancement of God's Kingdom.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="bg-ivory-alt">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-20">
          <Reveal><h2 className="font-display text-4xl font-semibold text-navy tracking-tight">Our values</h2></Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-xl bg-white border border-border p-7 shadow-sm">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-emerald/10 text-emerald">
                    <v.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-navy">{v.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 lg:px-8 py-20 lg:py-24">
        <Reveal>
          <div className="rounded-xl border border-emerald/20 bg-emerald/5 p-8">
            <Store className="h-7 w-7 text-emerald" />
            <h3 className="mt-4 font-display text-2xl font-semibold text-navy">Evangelism through everyday life</h3>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              As the body works together, the love of Jesus becomes visible — in restored places of
              worship, in strengthened ministries, in art that points to Christ, and in businesses run
              with integrity. Our hope is that more lives are reached with the Gospel through it all.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-10 text-sm text-muted-foreground border-t border-border pt-6">
            <strong className="text-navy">A note for review:</strong> Sections describing our history,
            leadership, statement of faith, and detailed stewardship approach are being prepared with
            our founders and will be published here once finalized. We do not publish names, dates, or
            claims until they are confirmed.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
